import { useState } from 'react';

import TABLE_CONSTANTS from '@constants/admin/tables';
import { Row } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';

import CreateBrandModal from '@/components/admin/CreateBrandModal';
import ResizableTable from '@/components/admin/ResizableTable';
import SectionHeading from '@/components/admin/SectionHeading';
import TextInput from '@/components/admin/TextInput';
import { ErrorTypeEnum } from '@/enums/error-type.enum';
import { Brand, SortEnumType, useGetBrandsQuery } from '@/graphql/main';
import usePaginate from '@/hooks/admin/usePaginate';
import useError from '@/hooks/error/useError';
import useColDefs from '@/pages/admin/CommercialAgreements/Brands/hooks/useColDefs';

const BrandsTable = () => {
    const {
        handlePageChange,
        pageSize,
        itemsToSkip,
        setTotalItems,
        totalItems,
        setSortingOrder,
        sortingOrder
    } = usePaginate();
    const { setError, clearError } = useError();
    const [tableData, setTableData] = useState<Brand[]>([]);
    const { t } = useTranslation('translation');
    const [searchText, setSearchText] = useState('');
    const { TableSize } = TABLE_CONSTANTS;

    const { loading, refetch } = useGetBrandsQuery({
        variables: {
            searchText,
            take: pageSize,
            skip: itemsToSkip,
            order: sortingOrder
        },
        onCompleted: (data) => {
            if (data?.brands?.items) {
                const nodes = data.brands.items.map((item) => item).filter(Boolean) as Brand[];
                setTableData(nodes);
                setTotalItems(data.brands.totalCount);
            }
            clearError();
        },
        onError: (error) => {
            setError(error, ErrorTypeEnum.ApolloError);
        }
    });

    const { colDef } = useColDefs({
        onChangeSuccess: async () => await refetch()
    });
    const onSortChange = (sorterResult: SorterResult<Brand>) =>
        setSortingOrder(
            sorterResult.order
                ? {
                      [sorterResult.columnKey as string]:
                          sorterResult.order === 'ascend' ? SortEnumType.Asc : SortEnumType.Desc
                  }
                : undefined
        );

    return (
        <>
            <SectionHeading heading={t('BRANDS_TABLE_TITLE')} />

            <Row justify={'space-between'} align={'bottom'} style={{ marginBottom: 18 }}>
                <TextInput inputValue={searchText} setInputValue={setSearchText} />
                <CreateBrandModal successCallback={refetch} />
            </Row>

            <ResizableTable
                onChange={(_, __, sorterResult) =>
                    onSortChange(sorterResult as SorterResult<Brand>)
                }
                colDefs={colDef}
                tableData={tableData}
                emptyText={t('NO_RESULTS')}
                loading={loading}
                onPageChange={handlePageChange}
                totalPaginationItems={totalItems}
                size={TableSize}
            />
        </>
    );
};

export default BrandsTable;
